import React, { useState } from 'react';
import { Stack, Typography, Box, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Papa from 'papaparse';
import { toast } from 'react-toastify';

import { Dropzone } from '../../../../components/dropzone';
import { GT_COLORS } from '../../../../constants/designTokens.constants';
import { CSV_TEMPLATE_SAMPLE_VALUES } from '../../../../constants/csvTemplateSampleData.constants';
import { bulkActionsApi } from '../../../../services/bulk-actions/bulkActionsApi';
import { handleApiError } from '../../../../services/react-query/react-query.client';
import { BulkDeletePreviewResponse } from '../../../../types/bulkActions.types';

const MAX_LINES = 1001; // 1000 data rows + 1 header

const IDENTIFIER_LABELS: Record<string, string> = {
    INVITECODE: 'phone number',
    EMAILADDRESS: 'email',
    EMPLOYEEID: 'employee ID',
};

const IDENTIFIER_TEMPLATE_COLUMNS: Record<string, string> = {
    INVITECODE: 'Phone (required)',
    EMAILADDRESS: 'Email (required)',
    EMPLOYEEID: 'Employee ID (required)',
};

const OPTIONAL_COLUMNS = ['First Name', 'Last Name', 'Full Name', 'Group', 'Team'];

interface UploadStepProps {
    identifierField: string;
    isSettingsLoading: boolean;
    onPreviewComplete: (data: BulkDeletePreviewResponse) => void;
}

export const UploadStep: React.FC<UploadStepProps> = ({
    identifierField,
    isSettingsLoading,
    onPreviewComplete,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [file64, setFile64] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    const label = IDENTIFIER_LABELS[identifierField] || 'unique identifier';

    const handleDownloadTemplate = () => {
        const identifierColumn = IDENTIFIER_TEMPLATE_COLUMNS[identifierField] || 'Identifier (required)';
        const allIdentifierColumns = ['Phone', 'Email', 'Employee ID'];
        const requiredLabel = identifierColumn.replace(' (required)', '');
        const optionalIdentifiers = allIdentifierColumns.filter(c => c !== requiredLabel);

        const columns = [identifierColumn, ...optionalIdentifiers, ...OPTIONAL_COLUMNS];

        const sampleRow: Record<string, string> = {};
        for (const col of columns) {
            sampleRow[col] = CSV_TEMPLATE_SAMPLE_VALUES[col] || '';
        }

        const csvContent = Papa.unparse({ fields: columns, data: [sampleRow] });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bulk-delete-users-template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleFileChange = (b64: string, file: File) => {
        // Validate file type
        const isCSV = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
        if (!isCSV) {
            toast.error('Invalid file type. Please upload a .csv file.');
            return;
        }

        // Count lines to enforce the 1,000-row cap
        try {
            const text = atob(b64.split(',')[1] || b64);
            const lineCount = text.split('\n').filter(line => line.trim() !== '').length;
            if (lineCount > MAX_LINES) {
                toast.error(`File has ${lineCount - 1} data rows, which exceeds the maximum of 1,000. Please reduce the file size.`);
                return;
            }
        } catch {
            toast.error('Could not read the file. Please try again.');
            return;
        }

        setFile64(b64);
        setSelectedFile(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        try {
            const data = await bulkActionsApi.deleteUsersPreview(selectedFile);
            onPreviewComplete(data);
        } catch (err: any) {
            const msg = handleApiError(err);
            toast.error(`Preview failed: ${msg}`);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Stack spacing={3} sx={{ mt: 2 }}>
            {/* How it works callout */}
            {!isSettingsLoading && (
                <Box
                    sx={{
                        p: 2.5,
                        borderLeft: `4px solid ${GT_COLORS.primary}`,
                        borderRadius: '4px',
                        backgroundColor: GT_COLORS.primaryLight,
                    }}
                >
                    <Typography fontSize={14} fontWeight={600} color={GT_COLORS.textPrimary} sx={{ mb: 0.5 }}>
                        How it works
                    </Typography>
                    <Typography fontSize={13} color={GT_COLORS.textSecondary} component="div">
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, lineHeight: 1.7 }}>
                            <li>Your organization uses <strong>{label}</strong> as the unique identifier to match users.</li>
                            <li>Additional columns (name, team, etc.) are optional and help verify the correct users.</li>
                            <li>All matched users will be permanently deleted. This action cannot be undone.</li>
                        </ul>
                    </Typography>
                </Box>
            )}

            {/* Template download */}
            <Box
                sx={{
                    p: 3,
                    border: `1px solid ${GT_COLORS.border}`,
                    borderRadius: '10px',
                    backgroundColor: GT_COLORS.cardBg,
                }}
            >
                <Typography fontSize={15} fontWeight={600} color={GT_COLORS.textPrimary} sx={{ mb: 1 }}>
                    CSV Template
                </Typography>
                <Typography fontSize={13} color={GT_COLORS.textSecondary} sx={{ mb: 2 }}>
                    Download a template with all supported columns. The {label} column is required; all others are optional
                    but recommended for verifying the correct users.
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Download Template
                </Button>
            </Box>

            {/* File upload */}
            <Box
                sx={{
                    p: 3,
                    border: `1px solid ${GT_COLORS.border}`,
                    borderRadius: '10px',
                    backgroundColor: GT_COLORS.cardBg,
                }}
            >
                <Typography fontSize={15} fontWeight={600} color={GT_COLORS.textPrimary} sx={{ mb: 2 }}>
                    Upload CSV
                </Typography>

                <Dropzone
                    file={file64 || undefined}
                    selectedFile={selectedFile || undefined}
                    onChange={handleFileChange}
                />

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                    <LoadingButton
                        variant="contained"
                        loading={isUploading}
                        disabled={!selectedFile || isSettingsLoading}
                        startIcon={<CloudUploadIcon />}
                        onClick={handleUpload}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                        Upload & Preview
                    </LoadingButton>
                </Stack>
            </Box>
        </Stack>
    );
};
